'use client';

import { useCallback, useEffect, useState } from 'react';
import { dictionaryCache } from '@/components/common/translation-provider';
import type { Locale } from '@/lib/i18n/config';
import { getDictionary } from '@/lib/i18n/dictionaries';
import type { TranslationKey } from '@/lib/i18n/utils';
import { clog } from '@/lib/logger/client';

type Dictionary = Awaited<ReturnType<typeof getDictionary>>;

interface UseTranslationsOptions {
  preloadedDictionary?: Dictionary;
}

export function useTranslations(
  locale: Locale,
  options?: UseTranslationsOptions,
) {
  const [dictionary, setDictionary] = useState<Dictionary | null>(
    options?.preloadedDictionary ?? dictionaryCache.get(locale) ?? null,
  );
  const [loading, setLoading] = useState(!dictionary);

  useEffect(() => {
    // The locale can change while a dictionary request is in flight (the
    // first render uses the default locale before the stored one is read).
    // Ignore responses for a locale that is no longer current, otherwise a
    // slower earlier request overwrites the selected language.
    let isStale = false;

    // If we have a preloaded dictionary, use it and skip loading
    if (options?.preloadedDictionary) {
      setDictionary(options.preloadedDictionary);
      dictionaryCache.set(locale, options.preloadedDictionary);
      setLoading(false);
      return;
    }

    // Check cache first
    const cached = dictionaryCache.get(locale);
    if (cached) {
      setDictionary(cached);
      setLoading(false);
      return;
    }

    // Load from API
    const loadDictionary = async () => {
      setLoading(true);
      try {
        const dict = await getDictionary(locale);
        dictionaryCache.set(locale, dict);
        if (!isStale) setDictionary(dict);
      } catch (error) {
        clog.error('Failed to load dictionary:', error);
        // Fallback to English or default locale
        try {
          const fallbackDict =
            locale !== 'en'
              ? await getDictionary('en')
              : await getDictionary('sv');
          dictionaryCache.set(locale, fallbackDict);
          if (!isStale) setDictionary(fallbackDict);
        } catch (fallbackError) {
          clog.error('Failed to load fallback dictionary:', fallbackError);
        }
      } finally {
        if (!isStale) setLoading(false);
      }
    };

    loadDictionary();

    return () => {
      isStale = true;
    };
  }, [locale, options?.preloadedDictionary]);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>) => {
      // If dictionary is not loaded yet, return empty string to avoid showing keys
      // With preloading, this should rarely happen, but provides graceful fallback
      if (!dictionary) {
        return '';
      }

      const keys = key.split('.');
      let value: any = dictionary;

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Key not found in dictionary - this is a missing translation
          // Return empty string to avoid showing the key to users
          // In development, you might want to log this
          if (process.env.NODE_ENV === 'development') {
            clog.warn(`Translation key not found: ${key}`);
          }
          return '';
        }
      }

      if (typeof value !== 'string') {
        // Invalid translation value
        if (process.env.NODE_ENV === 'development') {
          clog.warn(`Translation value is not a string for key: ${key}`);
        }
        return '';
      }

      // Replace parameters if provided
      // Support both {{param}} (i18next style) and {param} (simple style)
      if (params) {
        return Object.entries(params).reduce(
          (str, [param, replacement]) =>
            str
              .replace(new RegExp(`{{${param}}}`, 'g'), String(replacement))
              .replace(new RegExp(`{${param}}`, 'g'), String(replacement)),
          value,
        );
      }

      return value;
    },
    [dictionary],
  );

  return { t, loading, dictionary };
}
